import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star,
  Link,
  UserPlus,
  UserCheck,
  Heart,
  Lock,
  BadgeCheck,
  HandHeart,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { profileStatusUtils } from "../../services/queryClient";
import { useAuth } from "../../context/AuthContext";
import { toggleFollow } from "../../services/followService";
import { toggleAdmire, removeAdmire } from "../../services/admireService";
import { blockUser } from "../../services/blockService";
import AdmireModal from "./AdmireModal";
import ContactModal from "./ContactModal";
import StatsModal from "./StatsModal";
import DraftModal from "../Modal/DraftModal";
import ReportModal from "../Modal/Reportmodal"; 
import ProfileOptionsModal from "../Modal/ProfileOptionsModal";
import ErrorModal from "../Modal/ErrorModal";

const ResponsiveProfileImage = ({ imageUrls, alt, className }) => {
  const getImageUrl = (size) => {
    if (!imageUrls) return null;
    if (typeof imageUrls === "string") return imageUrls;
    return imageUrls[size] || imageUrls.medium || imageUrls.large;
  };

  const thumbnailUrl = getImageUrl("thumbnail");
  const smallUrl = getImageUrl("small");
  const mediumUrl = getImageUrl("medium");
  const largeUrl = getImageUrl("large");

  return (
    <picture>
      <source
        media="(max-width: 640px)"
        type="image/avif"
        srcSet={`${smallUrl || mediumUrl} 1x, ${mediumUrl || largeUrl} 2x`}
      />
      <source
        media="(max-width: 1024px)"
        type="image/avif"
        srcSet={`${mediumUrl || largeUrl} 1x, ${largeUrl} 2x`}
      />
      <source
        media="(min-width: 1025px)"
        type="image/avif"
        srcSet={`${largeUrl} 1x`}
      />
      <img
        src={mediumUrl || largeUrl || smallUrl || thumbnailUrl}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />
    </picture>
  );
};

const ProfileHeaderSection = ({
  setSidebarOpen,
  userData,
  onBack,
  queryClient,
  onFollowStatusChange,
}) => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [isFollowing, setIsFollowing] = useState(
    userData?.followStatus?.isFollowing || false,
  );
  const [isFollowAccepted, setIsFollowAccepted] = useState(
    userData?.followStatus?.isAccepted || 0,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showAdmireModal, setShowAdmireModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isAdmiring, setIsAdmiring] = useState(
    userData?.admireStatus?.isAdmiring || false,
  );
  const [currentAdmireType, setCurrentAdmireType] = useState(
    userData?.admireStatus?.admireType || null,
  );
  const [isAccepted, setIsAccepted] = useState(
    userData?.admireStatus?.isAccepted || null,
  );
  const [isAdmireLoading, setIsAdmireLoading] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [statsModalTab, setStatsModalTab] = useState("followers");
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportSuccessMsg, setReportSuccessMsg] = useState(null);
  const [showProfileOptions, setShowProfileOptions] = useState(false); 
  const [isBlocking, setIsBlocking] = useState(false); 
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  const admireOptions = [
    {
      icon: Star,
      label: "Inspiration",
      desc: "Their words spark ideas and push your creativity forward",
      value: "inspiration",
    },
    {
      icon: Sparkles,
      label: "Idol",
      desc: "A writer you deeply admire and aspire to become like",
      value: "idol",
    },
    {
      icon: Heart,
      label: "Adore",
      desc: "Someone whose writing you genuinely love and cherish",
      value: "adore",
    },
    {
      icon: HandHeart,
      label: "Support",
      desc: "A creator you believe in and want to encourage",
      value: "support",
    },
  ];

  const data = userData;
  const isOwnProfile = userData?.isOwnProfile;
  const isBlocked = userData?.isBlocked;

  useEffect(() => {
    if (
      userData?.followStatus !== undefined &&
      userData?.followStatus !== null
    ) {
      setIsFollowing(userData.followStatus.isFollowing);
      setIsFollowAccepted(userData.followStatus.isAccepted || 0);
    }
    if (
      userData?.admireStatus !== undefined &&
      userData?.admireStatus !== null
    ) {
      setIsAdmiring(userData.admireStatus.isAdmiring);
      setCurrentAdmireType(userData.admireStatus.admireType);
      setIsAccepted(userData.admireStatus.isAccepted);
    }
  }, [userData?.followStatus, userData?.admireStatus]);

  useEffect(() => {
    if (showAdmireModal || showContactModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showAdmireModal, showContactModal]);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = () => setShowProfileMenu(false);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showProfileMenu]);

  const handleFollowClick = async () => {
    setIsLoading(true);
    try {
      const result = await toggleFollow(apiCall, data.profileID);

      setIsFollowing(result.data.isFollowing);
      setIsFollowAccepted(result.data.isAccepted || 0);

      if (!result.data.isFollowing) {
        setIsAdmiring(false);
        setCurrentAdmireType(null);
        setIsAccepted(null);

        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          false,
          null,
          Math.max((data.admirers || 0) - 1, 0),
        );
      }

      profileStatusUtils.updateFollowStatus(
        data.profileID,
        result.data.isFollowing,
        result.data.isAccepted,
        result.data.newFollowerCount,
      );

      if (onFollowStatusChange) {
        onFollowStatusChange(result.data.isFollowing, result.data.isAccepted);
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        message: error.message || "We couldn't process this follow request. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdmireClick = async (option) => {
    if (isAdmiring && currentAdmireType !== option.value) {
      setShowAdmireModal(false);
      return;
    }
    setShowAdmireModal(false);
    setIsAdmireLoading(true);

    try {
      if (isAdmiring && currentAdmireType === option.value) {
        const result = await removeAdmire(apiCall, data.profileID);
        setIsAdmiring(false);
        setCurrentAdmireType(null);
        setIsAccepted(null);

        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          false,
          null,
          Math.max((data.admirers || 0) - 1, 0),
        );
      } else {
        const result = await toggleAdmire(
          apiCall,
          data.profileID,
          option.value,
        );
        setIsAdmiring(result.data.isAdmiring);
        setCurrentAdmireType(result.data.admireType);
        setIsAccepted(result.data.isAccepted);

        profileStatusUtils.updateAdmireStatus(
          data.profileID,
          result.data.isAdmiring,
          result.data.admireType,
          (data.admirers || 0) + 1,
        );
      }
    } catch (error) {
      setErrorModal({
        isOpen: true,
        message: error.message || "We couldn't process this admire request. Please try again.",
      });
    } finally {
      setIsAdmireLoading(false);
    }
  };

  const handleOpenReport = () => {
    setShowProfileOptions(false);
    setShowReportModal(true);
  };

  const handleBlockUser = async () => {
    setIsBlocking(true);
    const result = await blockUser(apiCall, data.profileID);
    setIsBlocking(false);

    if (!result.success) {
      setErrorModal({
        isOpen: true,
        message: result.error || "We couldn't block this user. Please try again.",
      });
      return;
    }

    setShowProfileOptions(false);

    // queryClient.removeQueries({ queryKey: ["feedPosts"] });
    // queryClient.removeQueries({ queryKey: ["explorePosts"] });
    // queryClient.removeQueries({ queryKey: ["trendingPosts"] });
    // queryClient.removeQueries({ queryKey: ["reviewRequests"] });
    // queryClient.removeQueries({ queryKey: ["collectionPosts"] });
    // queryClient.removeQueries({ queryKey: ["suggestedUsers"] });
    // queryClient.removeQueries({ queryKey: ["userPosts", data.profileID] });
    queryClient.invalidateQueries({ queryKey: ["feedPosts"] });
    queryClient.invalidateQueries({ queryKey: ["explorePosts"] });
    queryClient.invalidateQueries({ queryKey: ["trendingPosts"] });
    queryClient.invalidateQueries({ queryKey: ["reviewRequests"] });
    queryClient.invalidateQueries({ queryKey: ["collectionPosts"] });
    queryClient.invalidateQueries({ queryKey: ["suggestedUsers"] });
    queryClient.invalidateQueries({ queryKey: ["userPosts", data.profileID] });

    // Remove this profile from the cache and refetch — it'll come back
    await queryClient.refetchQueries({
      queryKey: ["profile", "username", userData.username],
      exact: true,
    });
  };

  const getInitials = (name) => {
    if (!name) return "";
    const words = name.trim().split(/\s+/);
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    }
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const getAdmireButtonLabel = () => {
    if (!isAdmiring) return "Admire";

    return (
      currentAdmireType?.charAt(0).toUpperCase() + currentAdmireType?.slice(1)
    );
  };

  const shouldShowLock = (statType) => {
    if (isOwnProfile) return false;

    const privacySettings = userData?.privacySettings || {};

    switch (statType) {
      case "followers":
        return privacySettings.showFollowers !== 1;
      case "following":
        return privacySettings.showFollowing !== 1;
      case "admirers":
        return privacySettings.showAdmirers !== 1;
      default:
        return false;
    }
  };

  const handleStatsClick = (tab) => {
    if (isOwnProfile || userData?.privacySettings?.isPrivateAccount === 0) {
      setStatsModalTab(tab);
      setShowStatsModal(true);
      return;
    }

    if (isFollowing && isFollowAccepted === 1) {
      setStatsModalTab(tab);
      setShowStatsModal(true);
    }
  };

  // ADD THIS ENTIRE BLOCK
  if (isBlocked) {
    return (
      <>
        <div className="bg-white px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 flex items-center justify-center text-black"
              aria-label="Go back"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>

            <h1 className="text-lg font-semibold text-black">{data.username}</h1>

            <div className="w-8 h-8" />
          </div>
        </div>

        <div className="bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-6 flex flex-col items-center text-center gap-3">
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
              <svg
                className="w-full h-full text-slate-400"
                viewBox="0 0 100 100"
                fill="currentColor"
              >
                <circle cx="50" cy="35" r="18" />
                <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
              </svg>
            </div>

            <p className="text-base font-semibold text-black">{data.username}</p>

            <div className="flex items-center justify-center gap-7 sm:gap-12 pt-2">
              <div className="text-center">
                <div className="text-xl font-semibold text-black">0</div>
                <div className="text-sm text-black">Posts</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-black">0</div>
                <div className="text-sm text-black">Followers</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-black">0</div>
                <div className="text-sm text-black">Following</div>
              </div>
              <div className="text-center">
                <div className="text-xl font-semibold text-black">0</div>
                <div className="text-sm text-black">Admirers</div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style jsx>{`
        @media (min-width: 1285px) {
          .custom-xl\\:hidden {
            display: none !important;
          }
        }

        .gradient-border {
          background: conic-gradient(
            from 0deg,
            #feda75,
            #fa7e1e,
            #d62976,
            #962fbf,
            #4f5bd5,
            #feda75
          );
          display: inline-block;
        }

        @media (max-width: 765px) {
          .mobile-center {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
          }
          .desktop-layout {
            display: none !important;
          }
        }

        @media (min-width: 766px) {
          .mobile-center {
            display: none !important;
          }
          .desktop-layout {
            display: flex !important;
          }
        }
      `}</style>

      <div className="bg-white px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isOwnProfile && (
              <button
                onClick={() => navigate(-1)}
                className="w-8 h-8 flex items-center justify-center text-black"
                aria-label="Go back"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
            )}
            {isOwnProfile && (
              <button
                className="w-8 h-8 flex items-center justify-center text-black custom-xl:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="5" x2="21" y2="5" />
                  <circle cx="14" cy="5" r="3" fill="black" />

                  <line x1="3" y1="12" x2="21" y2="12" />
                  <circle cx="8" cy="12" r="3" fill="black" />

                  <line x1="3" y1="19" x2="21" y2="19" />
                  <circle cx="17" cy="19" r="3" fill="black" />
                </svg>
              </button>
            )}

            {isOwnProfile && (
              <button
                onClick={() => navigate("/create-post")}
                className="w-8 h-8 hidden custom-xl:flex items-center justify-center text-black"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex-1 text-center flex items-center justify-center gap-1 relative">
            {isOwnProfile ? (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setShowProfileMenu((prev) => !prev);
                }}
                className="flex items-center justify-center gap-1 cursor-pointer"
              >
                <h1 className="text-lg font-semibold text-black">
                  {data.username.length > 20
                    ? data.username.slice(0, 20) + "..."
                    : data.username}
                </h1>

                {data.verified && (
                  <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />
                )}

                <ChevronDown className="w-5 h-5 text-black" />
              </div>
            ) : (
              <>
                <h1 className="text-lg font-semibold text-black">
                  {data.username.length > 20
                    ? data.username.slice(0, 20) + "..."
                    : data.username}
                </h1>

                {data.verified && (
                  <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />
                )}
              </>
            )}

            {isOwnProfile && showProfileMenu && (
              <div
                onClick={(e) => e.stopPropagation()}
                className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-white rounded-lg shadow-lg border border-gray-100 w-48 z-50 overflow-hidden"
              >
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate("/r/review-requests");
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-black hover:bg-gray-50 transition-colors"
                >
                  Review Requests
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    setShowDraftModal(true);
                  }}
                  className="w-full text-left px-4 py-3 text-sm text-black hover:bg-gray-50 border-t border-gray-100 transition-colors"
                >
                  Draft Reviews
                </button>
              </div>
            )}
          </div>

          {/* <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/settings")}
              className="w-8 h-8 flex items-center justify-center text-black font-semibold"
              aria-label="Settings"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="black"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="3" fill="black" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
          </div> */}
          <div className="flex items-center gap-4">
            {isOwnProfile ? (
              <button
                onClick={() => navigate("/settings")}
                className="w-8 h-8 flex items-center justify-center text-black font-semibold"
                aria-label="Settings"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3" fill="black" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>
            ) : (
              <button
                onClick={() => setShowProfileOptions(true)}
                className="w-8 h-8 flex items-center justify-center text-black"
                aria-label="More options"
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <line
                    x1="5"
                    y1="7"
                    x2="19"
                    y2="7"
                    stroke="black"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="9"
                    y1="12"
                    x2="15"
                    y2="12"
                    stroke="black"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="5"
                    y1="17"
                    x2="19"
                    y2="17"
                    stroke="black"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-100 shadow-sm pt-16">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
          <div className="desktop-layout items-start gap-3 sm:gap-4 md:gap-6">
            <div className="relative flex-shrink-0">
              <div className="gradient-border p-[3px] rounded-full inline-block">
                <div className="w-20 h-20 sm:w-28 sm:h-28 md:w-32 md:h-32 lg:w-40 lg:h-40 rounded-full bg-white p-[3px] overflow-hidden">
                  {data.avatar ? (
                    <ResponsiveProfileImage
                      imageUrls={data.avatar}
                      alt={data.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                      {data.name ? (
                        <span className="text-slate-600 font-semibold text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
                          {getInitials(data.name)}
                        </span>
                      ) : (
                        <svg
                          className="w-full h-full text-slate-400"
                          viewBox="0 0 100 100"
                          fill="currentColor"
                        >
                          <circle cx="50" cy="35" r="18" />
                          <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 pt-1 sm:pt-1 min-w-0">
              <div className="flex items-start justify-between gap-2 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-semibold text-black truncate mb-1">
                    {data.name}
                  </h1>

                  <div className="flex items-center gap-1 mb-1 sm:mb-1">
                    <p className="text-xs sm:text-sm md:text-base font-400 text-black truncate">
                      {data.username}
                    </p>
                    {data.verified && (
                      <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />
                    )}
                  </div>

                  <p className="text-sm sm:text-base font-400 text-black mb-1 sm:mb-1 leading-relaxed line-clamp-3">
                    {data.bio}
                  </p>

                  <div className="flex flex-row items-center gap-4 text-sm text-black">
                    {data.website && (
                      <div className="flex items-center gap-1">
                        <Link className="w-4 h-4 flex-shrink-0" />
                        <a
                          href={
                            data.website.startsWith("http")
                              ? data.website
                              : `https://${data.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline truncate"
                        >
                          {data.website.replace(/^https?:\/\//, "")}
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 flex items-center gap-2">
                  {isOwnProfile ? (
                    <button
                      onClick={() => navigate("/edit-profile")}
                      className="px-6 py-2 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 text-white rounded-lg flex items-center gap-2 font-semibold text-sm shadow-md"
                    >
                      <span>Edit Profile</span>
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleFollowClick}
                        disabled={isLoading}
                        className={`
                          px-8 py-1 lg:px-10 lg:py-1.5 rounded-md text-sm font-semibold
                          transition-all duration-300 flex items-center justify-center gap-2 active:scale-95
                          ${
                            isFollowing
                              ? "bg-gray-200 text-black hover:bg-gray-300"
                              : "bg-[#b0d284] text-black hover:bg-[#9ec170]"
                          }
                          ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                        `}
                      >
                        {isLoading ? (
                          <div className="w-4 h-4 border-2 border-black/60 border-t-transparent rounded-full animate-spin" />
                        ) : isFollowing ? (
                          <>
                            <UserCheck className="w-4 h-4" />
                            <span>
                              {isFollowAccepted === 1
                                ? "Following"
                                : "Requested"}
                            </span>
                          </>
                        ) : (
                          <>
                            <UserPlus className="w-4 h-4" />
                            <span>Follow</span>
                          </>
                        )}
                      </button>
                      {isFollowing && isFollowAccepted === 1 && (
                        <button
                          onClick={() => setShowAdmireModal(true)}
                          disabled={isAdmireLoading}
                          className={`
                          px-6 py-1 lg:px-8 lg:py-1.5 rounded-md font-semibold text-sm shadow-md 
                          transition-all duration-300 flex items-center gap-2
                          ${
                            isAdmiring
                              ? "bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600"
                              : "bg-gradient-to-r from-pink-100 to-red-100 text-pink-700 hover:from-pink-200 hover:to-red-200"
                          }
                          ${
                            isAdmireLoading
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }
                        `}
                        >
                          {isAdmireLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Heart
                                className={`w-4 h-4 ${
                                  isAdmiring ? "fill-current" : ""
                                }`}
                              />
                              <span>{getAdmireButtonLabel()}</span>
                            </>
                          )}
                        </button>
                      )}
                      {isFollowing &&
                        isFollowAccepted === 1 &&
                        userData?.privacySettings?.showContactButton === 1 && (
                          <button
                            onClick={() => setShowContactModal(true)}
                            className="px-6 py-1 lg:px-8 lg:py-1.5 rounded-md font-semibold text-sm transition-all duration-300 flex items-center gap-2 bg-gray-200 text-black hover:bg-gray-300"
                          >
                            <span>Contact</span>
                          </button>
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mobile-center">
            <div className="relative mb-2" style={{ marginTop: "-50px" }}>
              <div className="gradient-border p-[3px] rounded-full">
                <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-white p-[3px] overflow-hidden">
                  {data.avatar ? (
                    <ResponsiveProfileImage
                      imageUrls={data.avatar}
                      alt={data.name}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                      {data.name ? (
                        <span className="text-slate-600 font-semibold text-5xl sm:text-6xl">
                          {getInitials(data.name)}
                        </span>
                      ) : (
                        <svg
                          className="w-full h-full text-slate-400"
                          viewBox="0 0 100 100"
                          fill="currentColor"
                        >
                          <circle cx="50" cy="35" r="18" />
                          <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold text-black">
                {data.name}
              </h1>
              <div className="flex items-center justify-center gap-1 mb-1 flex-wrap">
                <p className="text-base font-400 text-black">
                  {data.username}
                </p>
                {data.verified && (
                  <BadgeCheck className="w-4 h-4 fill-[#b0d284] stroke-white" />
                )}
              </div>
            </div>

            <p className="text-sm font-400 text-black mb-1 leading-relaxed px-4 max-w-md">
              {data.bio}
            </p>

            <div className="flex items-center justify-center gap-4 mb-4 text-sm text-black px-4">
              {data.website && (
                <div className="flex items-center gap-1">
                  <Link className="w-4 h-4 flex-shrink-0" />
                  <a
                    href={
                      data.website.startsWith("http")
                        ? data.website
                        : `https://${data.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline truncate"
                  >
                    {data.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mb-4">
              {isOwnProfile ? (
                <>
                  <button
                    onClick={() => navigate("/edit-profile")}
                    className="px-6 py-2 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 text-white rounded-lg flex items-center gap-2 font-semibold text-sm shadow-md"
                  >
                    <span>Edit Profile</span>
                  </button>
                  <button
                    onClick={() => navigate("/settings")}
                    className="w-8 h-8 text-black"
                    aria-label="Settings"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="3" fill="black" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.01a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleFollowClick}
                    disabled={isLoading}
                    className={`
                      ${isFollowing ? "flex-1" : "w-full"}
                      px-8 py-1.5 rounded-md text-sm font-semibold
                      transition-all duration-200
                      flex items-center justify-center gap-2
                      ${
                        isFollowing
                          ? "bg-gray-200 text-black hover:bg-gray-300"
                          : "bg-[#b0d284] text-black hover:bg-[#9ec170]"
                      }
                      ${isLoading ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                  >
                    {isLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : isFollowing ? (
                      <>
                        <UserCheck className="w-4 h-4" />
                        <span>
                          {isFollowAccepted === 1 ? "Following" : "Requested"}
                        </span>
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4" />
                        <span>Follow</span>
                      </>
                    )}
                  </button>
                  {isFollowing && isFollowAccepted === 1 && (
                    <button
                      onClick={() => setShowAdmireModal(true)}
                      disabled={isAdmireLoading}
                      className={`
                      flex-1 px-6 py-1.5 rounded-md font-semibold text-sm shadow-md 
                      transition-all duration-300 flex items-center justify-center gap-2
                      ${
                        isAdmiring
                          ? "bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600"
                          : "bg-gradient-to-r from-pink-100 to-red-100 text-pink-700 hover:from-pink-200 hover:to-red-200"
                      }
                      ${isAdmireLoading ? "opacity-50 cursor-not-allowed" : ""}
                    `}
                    >
                      {isAdmireLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Heart
                            className={`w-4 h-4 ${
                              isAdmiring ? "fill-current" : ""
                            }`}
                          />
                          <span>{getAdmireButtonLabel()}</span>
                        </>
                      )}
                    </button>
                  )}
                  {isFollowing &&
                    isFollowAccepted === 1 &&
                    userData?.privacySettings?.showContactButton === 1 && (
                      <button
                        onClick={() => setShowContactModal(true)}
                        className="px-6 py-1.5 rounded-md font-semibold text-sm flex items-center bg-gray-100 text-black"
                      >
                        <span>Contact</span>
                      </button>
                    )}
                </>
              )}
            </div>

            <div className="w-full pt-2">
              <div className="w-full pt-1 flex justify-center space-x-7 sm:space-x-12">
                <div className="text-center">
                  <div className="text-xl font-semibold text-black">
                    {data.totalPosts.toLocaleString()}
                  </div>
                  <div className="text-sm text-black">Posts</div>
                </div>
                <div
                  onClick={() => handleStatsClick("followers")}
                  className="text-center cursor-pointer"
                >
                  <div className="text-xl font-semibold text-black">
                    {shouldShowLock("followers") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      data.followers.toLocaleString()
                    )}
                  </div>
                  <div className="text-sm text-black">Followers</div>
                </div>

                <div
                  onClick={() => handleStatsClick("following")}
                  className="text-center cursor-pointer"
                >
                  <div className="text-xl font-semibold text-black">
                    {shouldShowLock("following") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      data.following.toLocaleString()
                    )}
                  </div>
                  <div className="text-sm text-black">Following</div>
                </div>

                <div
                  onClick={() => handleStatsClick("admirers")}
                  className="text-center cursor-pointer"
                >
                  <div className="text-xl font-semibold text-black">
                    {shouldShowLock("admirers") ? (
                      <Lock className="w-6 h-6 mx-auto text-gray-400 mb-1" />
                    ) : (
                      data.admirers.toLocaleString()
                    )}
                  </div>
                  <div className="text-sm text-black">Admirers</div>
                </div>
              </div>
            </div>
          </div>

          <div className="hidden desktop-stats mt-4 sm:mt-6 md:mt-4 sm:pt-4 md:pt-3">
            <div className="grid grid-cols-4 gap-2 sm:gap-4 md:gap-6 lg:gap-8">
              <div className="text-center group">
                <div className="text-2xl xl:text-3xl font-semibold text-black">
                  {data.totalPosts.toLocaleString()}
                </div>
                <div className="text-sm text-black">Posts</div>
              </div>

              <div
                onClick={() => handleStatsClick("followers")}
                className="text-center group cursor-pointer"
              >
                <div className="text-2xl xl:text-3xl font-semibold text-black">
                  {shouldShowLock("followers") ? (
                    <Lock className="w-7 h-7 xl:w-8 xl:h-8 mx-auto text-gray-400 mb-1" />
                  ) : (
                    data.followers.toLocaleString()
                  )}
                </div>
                <div className="text-sm text-black">Followers</div>
              </div>

              <div
                onClick={() => handleStatsClick("following")}
                className="text-center group cursor-pointer"
              >
                <div className="text-2xl xl:text-3xl font-semibold text-black">
                  {shouldShowLock("following") ? (
                    <Lock className="w-7 h-7 xl:w-8 xl:h-8 mx-auto text-gray-400 mb-1" />
                  ) : (
                    data.following.toLocaleString()
                  )}
                </div>
                <div className="text-sm text-black">Following</div>
              </div>

              <div
                onClick={() => handleStatsClick("admirers")}
                className="text-center group cursor-pointer"
              >
                <div className="text-2xl xl:text-3xl font-semibold text-black">
                  {shouldShowLock("admirers") ? (
                    <Lock className="w-7 h-7 xl:w-8 xl:h-8 mx-auto text-gray-400 mb-1" />
                  ) : (
                    data.admirers.toLocaleString()
                  )}
                </div>
                <div className="text-sm text-black">Admirers</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdmireModal
        showAdmireModal={showAdmireModal}
        setShowAdmireModal={setShowAdmireModal}
        admireOptions={admireOptions}
        handleAdmireClick={handleAdmireClick}
        isAdmiring={isAdmiring}
        currentAdmireType={currentAdmireType}
        userName={data.username}
      />

      <ContactModal
        showContactModal={showContactModal}
        setShowContactModal={setShowContactModal}
        userData={data}
      />

      <StatsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        initialTab={statsModalTab}
        profileData={data}
      />

      <DraftModal
        isOpen={showDraftModal}
        onClose={() => setShowDraftModal(false)}
      />

      {showProfileOptions && (
        <ProfileOptionsModal
          onClose={() => setShowProfileOptions(false)}
          onReport={handleOpenReport}
          onBlock={handleBlockUser}
          isBlocking={isBlocking}
        />
      )}

      {showReportModal && (
        <ReportModal
          targetId={data.profileID}
          targetType="user"
          onClose={() => setShowReportModal(false)}
          onSuccess={(message) => {
            setShowReportModal(false);
            setReportSuccessMsg(message);
            setTimeout(() => setReportSuccessMsg(null), 3000);
          }}
        />
      )}

      {reportSuccessMsg && (
        <div className="fixed bottom-6 inset-x-4 z-[70] flex justify-center">
          <div className="w-full max-w-md bg-black text-white text-sm font-medium px-4 py-3 rounded-full shadow-lg text-center">
            {reportSuccessMsg}
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />

      <style jsx>{`
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @media (min-width: 766px) {
          .desktop-stats {
            display: block !important;
          }
        }
      `}</style>
    </>
  );
};

export default ProfileHeaderSection;
